/**
 * What is out, and with whom — the owner's view across every work they hold.
 *
 * The collector's rail called this "Loans, Consign & Agents" and marked it
 * `planned` from the day the portal shipped, with a page saying what had to
 * exist first. After the transfers migration the missing piece narrowed to one
 * read — `ondo.my_arrangements()` — and this is the screen that reads it.
 *
 * TWO GROUPS, NOT THREE. The rail's label names loans, and there is no
 * `ondo.loans` table: a loan to an institution is not a relation the registry
 * holds yet, so a group for it would be an empty heading over a promise. The
 * label is kept because it is what the design says and what the reader looks
 * for; the copy under it says plainly what this screen does and does not
 * carry, the way the activity log's "Not recorded here" does.
 */
export const ARRANGEMENTS_COPY = {
  loading: "Reading what is out…",
  errorTitle: "Can’t load your arrangements",

  empty: "Nothing is out.",
  emptyDetail:
    "Every work you own is held by you alone — no agent represents one, and none is with a gallery. When you name an agent from a work’s record, or a gallery accepts a work on consignment, it appears here.",

  /** 'what is out · 3 arrangements'. */
  summary: (n: number) => `${n} ${n === 1 ? "arrangement" : "arrangements"}`,

  lead: "Two kinds of arrangement, one list.",
  leadDetail:
    "An agent may write a work’s history and answer questions about it; a gallery holds the work itself and may sell it. Both are recorded on the work’s own record, which is where each one is ended — this screen shows the collection at once and links there.",

  groups: {
    mandates: "Represented by an agent",
    mandatesNote: "An agent acts on the record. The work stays with you.",
    consignments: "With a gallery",
    consignmentsNote:
      "The work is with the gallery, or offered to it. A sale is recorded from their side.",
  },

  /** The state chip on a consignment row. A mandate has one state and no chip. */
  state: {
    offered: "Offered — awaiting the gallery",
    accepted: "Consigned",
  },

  /** 'since 4 Feb 2026' / 'offered 4 Feb 2026'. */
  since: "since",
  offeredOn: "offered",

  /** Matched to `ondo.consignment_arrangement` and `ondo.consignment_basis`. */
  arrangement: {
    consignment: "Consignment",
    primary_representation: "Primary representation",
  },
  basis: {
    net_of_sale: "net-of-sale",
    min_price_agreed: "minimum price agreed",
    unspecified: "",
  },

  /**
   * Says what is NOT here, so an absence reads as a gap and not a policy.
   *
   * Loans are named because the rail names them. There is no table for one,
   * and an empty group under a heading would promise a record the registry
   * does not keep.
   */
  notHere: {
    title: "Not recorded here",
    points: [
      "Loans. Lending a work to an institution is not a relation the registry holds yet, so nothing here can say a work is on loan.",
      "Sales. A work you have sold is no longer yours, and its arrangement ended with the sale — the record is on the buyer’s side now.",
      "What was out. This lists live arrangements only; an agent who was stood down or a consignment that ended is in the activity log.",
    ],
  },

  open: "Open record",
} as const;
